import type {
  AlertAttributionFilters,
  HTTPPathMatchMode,
} from '@/types/alertAttribution';

export type HTTPFailureFilterKey =
  | 'statusCodes'
  | 'requestPaths'
  | 'routes'
  | 'userIds'
  | 'deviceIds'
  | 'roomIds'
  | 'reasons'
  | 'clientVersions';

export interface HTTPFailureFilterRow {
  id: number;
  key?: HTTPFailureFilterKey;
  values: string[];
  matchMode?: HTTPPathMatchMode;
}

export const HTTP_FAILURE_FILTER_OPTIONS: Array<{
  value: HTTPFailureFilterKey;
  label: string;
  placeholder: string;
}> = [
  {
    value: 'statusCodes',
    label: '具体错误码',
    placeholder: '输入多个 4xx/5xx 错误码，回车或逗号分隔，例如 404,409',
  },
  {
    value: 'requestPaths',
    label: '接口路径',
    placeholder: '输入 route 或实际 URL Path 后回车',
  },
  {
    value: 'routes',
    label: 'Route',
    placeholder: '输入精确 Route 后回车',
  },
  {
    value: 'userIds',
    label: '用户 ID',
    placeholder: '输入 userId 后回车',
  },
  {
    value: 'deviceIds',
    label: '设备 ID',
    placeholder: '输入 deviceId 后回车',
  },
  {
    value: 'roomIds',
    label: '房间 ID',
    placeholder: '输入 roomId 后回车',
  },
  {
    value: 'reasons',
    label: 'Reason',
    placeholder: '输入 reason 后回车',
  },
  {
    value: 'clientVersions',
    label: '客户端版本',
    placeholder: '输入 clientVersion 后回车',
  },
];

export const HTTP_PATH_MATCH_MODE_OPTIONS: Array<{
  value: HTTPPathMatchMode;
  label: string;
}> = [
  { value: 'exact', label: '精确' },
  { value: 'contains', label: '包含' },
  { value: 'regex', label: '正则' },
];

const MAX_FILTER_VALUES = 50;

function uniqueValues(values: string[]) {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

export function compileHTTPFailureFilters(rows: HTTPFailureFilterRow[]): {
  filters: AlertAttributionFilters;
  error?: string;
} {
  const filters: AlertAttributionFilters = {};
  for (const row of rows) {
    if (!row.key) continue;
    const values = uniqueValues(row.values);
    if (values.length > MAX_FILTER_VALUES) {
      return {
        filters,
        error: `${HTTP_FAILURE_FILTER_OPTIONS.find((item) => item.value === row.key)?.label ?? row.key}最多支持 ${MAX_FILTER_VALUES} 个值`,
      };
    }
    if (row.key === 'statusCodes') {
      const statusCodes = values.map((value) => Number(value));
      if (
        statusCodes.some(
          (value) => !Number.isInteger(value) || value < 400 || value > 599,
        )
      ) {
        return {
          filters,
          error: '错误码必须是 400–599 之间的整数',
        };
      }
      filters.statusCodes = statusCodes;
      continue;
    }
    if (row.key === 'requestPaths') {
      filters.requestPaths = values;
      filters.requestPathMatchMode = row.matchMode ?? 'exact';
      continue;
    }
    filters[row.key] = values;
  }
  return { filters };
}

export function filterValuePlaceholder(key?: HTTPFailureFilterKey) {
  return (
    HTTP_FAILURE_FILTER_OPTIONS.find((item) => item.value === key)
      ?.placeholder ?? '请先选择筛选 Key'
  );
}
